import React from "react";
import { Modal, Form, Input, Row, Col, Button, Select } from "antd";
import {
  ApartmentOutlined,
  FileTextOutlined,
  FolderAddOutlined,
} from "@ant-design/icons";
import { AffaireService } from "../../services/AffaireService";
import Toast from "../../helpers/Toast";
import { NavLink, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { catchError } from "../../services/DaoService";
import CustomFormItem from "../utils/form/CustomFormItem";
import { AiNameTrigger, AiNamePanel } from "../ai/AiNameSuggest";
import { useAiNameAssist } from "../../hooks/useAiNameAssist";
import AiDescriptionSuggest from "../ai/AiDescriptionSuggest";
import style from "./AffaireCreateForm.module.scss";
import { PrioriteService } from "../../services/PrioriteService";
import { ModeleService } from "../../services/ModeleService";
import { useUserContext } from "../../contexts/UserContext";
import { DepartementService } from "../../services/DepartementService";
import { affairesState } from "../../state/actions/affairesState";

const AffaireCreateForm = ({ visible, onCreate, onCancel, departement }) => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const nomValue = Form.useWatch('nom', form);
  const nameAi = useAiNameAssist('affaire');
  const [creating, setCreating] = React.useState(false);
  const [validationErrors, setValidationErrors] = React.useState({});
  const [modeles, setModeles] = React.useState([]);
  const [priorites, setPriorites] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const { currentUser, check } = useUserContext();
  const [departements, setDepartements] = React.useState([]);

  React.useEffect(() => {
    if (departement) {
      form.setFieldsValue({ departement_id: departement.id });
    }
  }, [departement, form]);

  React.useEffect(() => {
    // select the default modele (modele.default = 1)
    if (modeles.length) {
      form.setFieldsValue({
        modele_id: modeles.find((modele) => modele.default)?.id
      });
    }
  }, [modeles, form]);

  React.useEffect(() => {
    if (!visible) return;
    setLoading(true);
    Promise.all([
      ModeleService.getInstance().all(),
      PrioriteService.getInstance().all(),
      DepartementService.getInstance().all()
    ]).then(([modeles, priorites, departementData]) => {
      setModeles(modeles.data);
      setPriorites(priorites.data);
      setDepartements(departementData.data);
    }).catch(catchError).finally(() => {
      setLoading(false);
    });
  }, [visible]);

  const handleSave = (isRedirection) => {
    setCreating(true);
    form
      .validateFields()
      .then((values) => {
        AffaireService.getInstance()
          .create(values)
          .then((response) => {
            Toast.success(response.message);
            form.resetFields();
            affairesState.addAffaire(response.data);
            onCreate(response.data);
            if (isRedirection) {
              navigate(`/affaire/${response.data.uid}`);
            }
          })
          .catch((error) => {
            const errors = error.response.data;
            if (errors.validation && Object.keys(errors?.errors).length) {
              setValidationErrors(errors.errors);
            } else {
              catchError(error);
            }
          })
          .finally(() => {
            setCreating(false);
          });
      })
      .catch(() => {
        setCreating(false);
      });
  };

  React.useEffect(() => {
    if (!visible) return;
    form.setFieldsValue({
      responsable_id: currentUser.id
    });
  }, [currentUser, form, visible]);

  React.useEffect(() => {
    if (!visible) return;
    form.setFieldsValue({
      budget: 0
    });
    if (priorites.length) {
      form.setFieldsValue({
        priorite_id: priorites[0].id
      });
    }
  }, [priorites, form, visible]);

  return (
    <Modal
      destroyOnHidden
      open={visible}
      onCancel={onCancel}
      title={(
        <div className={style.modalTitle}>
          <span className={style.titleIcon} aria-hidden="true">
            <FolderAddOutlined />
          </span>
          <span>
            <span className={style.titleText}>Nouveau dossier</span>
            <span className={style.titleSubtitle}>Renseignez les informations essentielles pour lancer le suivi.</span>
          </span>
        </div>
      )}
      width={760}
      centered
      className={style.affaireModal}
      wrapClassName={style.affaireModalWrap}
      footer={
        <div className={style.modalFooter}>
          <Button className={style.cancelBtn} onClick={onCancel}>Annuler</Button>
          <Button
            type="primary"
            icon={<FolderAddOutlined />}
            className={style.submitBtn}
            loading={creating}
            onClick={() => handleSave(true)}
          >
            Créer le dossier
          </Button>
        </div>
      }
    >
      <Form
        layout="vertical"
        form={form}
        name="createAffaireForm"
        initialValues={{ enabled: 1 }}
        className={style.form}
      >

        <Row gutter={[16, 4]} className={style.formGrid}>
          <Col xs={24}>
            <CustomFormItem
              label="Nom du dossier"
              name="nom"
              rules={[{ required: true, message: "Champ requis" }]}
              validationErrors={validationErrors}
              setValidationErrors={setValidationErrors}
            >
              <Input
                className={style.control}
                prefix={<FileTextOutlined />}
                placeholder="Saisir le nom du dossier"
                suffix={
                  <AiNameTrigger
                    loading={nameAi.loading}
                    disabled={!nomValue || nomValue.trim().length < 3}
                    onClick={() => nameAi.run(form.getFieldValue('nom'))}
                  />
                }
              />
            </CustomFormItem>
            <AiNamePanel
              status={nameAi.status}
              proposal={nameAi.proposal}
              onApply={(text) => { form.setFieldsValue({ nom: text }); nameAi.reset(); }}
              onReset={nameAi.reset}
            />
          </Col>
          <Col xs={24} sm={12}>
            <CustomFormItem
              label={(
                <span className={style.fieldLabel}>
                  <span>Type de dossier</span>
                  {!!check('VIEW-SETTINGS') && (
                    <NavLink to="/modele" className={style.fieldLink}>Créer un type</NavLink>
                  )}
                </span>
              )}
              name="modele_id"
              rules={[{ required: true, message: "Champ requis" }]}
              validationErrors={validationErrors}
              setValidationErrors={setValidationErrors}
            >
              <Select className={style.control} showSearch optionFilterProp="label" loading={loading}
                placeholder="Sélectionner le type"
                options={modeles.map((m) => ({ label: m.nom, value: m.id }))} />
            </CustomFormItem>
          </Col>
          <Col xs={24} sm={12}>
            <CustomFormItem
              label={(
                <span className={style.fieldLabel}>
                  <span>Département</span>
                  <span className={style.optionalBadge}>Optionnel</span>
                </span>
              )}
              name="departement_id"
              validationErrors={validationErrors}
              setValidationErrors={setValidationErrors}
            >
              <Select className={style.control} showSearch optionFilterProp="searchLabel" allowClear
                placeholder="Sélectionner le département" loading={loading}
                options={departements.map((d) => ({
                  label: (
                    <span className={style.optionInline}>
                      <ApartmentOutlined />
                      <span>{d.nom} ({d.code})</span>
                    </span>
                  ),
                  value: d.id,
                  searchLabel: `${d.nom} (${d.code})`,
                }))} />
            </CustomFormItem>
          </Col>
          <Col xs={24}>
            <CustomFormItem
              label="Description (optionnel)"
              name="description"
              validationErrors={validationErrors}
              setValidationErrors={setValidationErrors}
            >
              <Input.TextArea className={style.textarea} rows={3} placeholder="Décrire brièvement le dossier..." />
            </CustomFormItem>
            <AiDescriptionSuggest
              type="affaire"
              getNom={() => form.getFieldValue('nom')}
              getCurrent={() => form.getFieldValue('description')}
              onApply={(text) => form.setFieldsValue({ description: text })}
            />
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default AffaireCreateForm;

AffaireCreateForm.propTypes = {
  visible: PropTypes.bool,
  onCreate: PropTypes.func,
  onCancel: PropTypes.func,
  departement: PropTypes.object,
};
